import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useStoreInfo, useUpdateStore } from '../hooks/useSettings';
import {
  BUSINESS_HOURS_MAX_LENGTH,
  MAX_STORE_PHONES,
  PHONE_LABEL_MAX_LENGTH,
  PHONE_NUMBER_MAX_LENGTH,
  StorePhone,
} from '../services/settingsService';

interface FormState {
  websiteUrl: string;
  contactEmail: string;
  contactPhone: string;
  businessHours: string;
  phones: StorePhone[];
}

const EMPTY: FormState = {
  websiteUrl: '',
  contactEmail: '',
  contactPhone: '',
  businessHours: '',
  phones: [],
};

function fromStore(store: ReturnType<typeof useStoreInfo>['data']): FormState {
  if (!store) return EMPTY;
  return {
    websiteUrl: store.websiteUrl ?? '',
    contactEmail: store.contactEmail ?? '',
    contactPhone: store.contactPhone ?? '',
    businessHours: store.businessHours ?? '',
    phones: store.phones.map((p) => ({ label: p.label ?? '', number: p.number ?? '' })),
  };
}

function isEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function isUrl(value: string): boolean {
  // Accept anything that already includes a scheme; reject obviously broken strings.
  return /^https?:\/\/\S+$/i.test(value);
}

function normalizePhones(list: StorePhone[]): StorePhone[] {
  return list
    .map((p) => ({ label: p.label.trim(), number: p.number.trim() }))
    .filter((p) => p.number);
}

export default function ContactSettings() {
  const { data: store, isLoading } = useStoreInfo();
  const updateStore = useUpdateStore();

  const [draft, setDraft] = useState<FormState>(EMPTY);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (!hydrated && store) {
      setDraft(fromStore(store));
      setHydrated(true);
    }
  }, [hydrated, store]);

  if (isLoading || !hydrated) {
    return <div className="py-8 text-center text-ink-3">Loading...</div>;
  }

  const saved = fromStore(store);
  const phonesDirty =
    JSON.stringify(normalizePhones(draft.phones)) !== JSON.stringify(normalizePhones(saved.phones));
  const dirty =
    draft.websiteUrl.trim() !== saved.websiteUrl ||
    draft.contactEmail.trim() !== saved.contactEmail ||
    draft.contactPhone.trim() !== saved.contactPhone ||
    draft.businessHours.trim() !== saved.businessHours ||
    phonesDirty;

  const errors: Partial<Record<'websiteUrl' | 'contactEmail' | 'businessHours', string>> = {};
  if (draft.contactEmail && !isEmail(draft.contactEmail)) {
    errors.contactEmail = 'Looks like an invalid email address.';
  }
  if (draft.websiteUrl && !isUrl(draft.websiteUrl)) {
    errors.websiteUrl = 'Use the full URL including https://';
  }
  if (draft.businessHours.length > BUSINESS_HOURS_MAX_LENGTH) {
    errors.businessHours = `Keep it under ${BUSINESS_HOURS_MAX_LENGTH} characters.`;
  }

  const phoneErrors = draft.phones.map((p) => {
    const e: Partial<Record<keyof StorePhone, string>> = {};
    if (p.label.length > PHONE_LABEL_MAX_LENGTH) e.label = `Max ${PHONE_LABEL_MAX_LENGTH} chars.`;
    if (p.number.length > PHONE_NUMBER_MAX_LENGTH) e.number = `Max ${PHONE_NUMBER_MAX_LENGTH} chars.`;
    return e;
  });
  const hasErrors = Object.keys(errors).length > 0 || phoneErrors.some((e) => Object.keys(e).length > 0);
  const atPhoneCap = draft.phones.length >= MAX_STORE_PHONES;

  const updatePhone = (idx: number, field: keyof StorePhone, value: string) => {
    setDraft((prev) => ({
      ...prev,
      phones: prev.phones.map((p, i) => (i === idx ? { ...p, [field]: value } : p)),
    }));
  };
  const removePhone = (idx: number) => {
    setDraft((prev) => ({ ...prev, phones: prev.phones.filter((_, i) => i !== idx) }));
  };
  const addPhone = () => {
    if (atPhoneCap) return;
    setDraft((prev) => ({ ...prev, phones: [...prev.phones, { label: '', number: '' }] }));
  };

  const handleSave = async () => {
    if (hasErrors) return;
    const cleanedPhones = normalizePhones(draft.phones);
    try {
      await updateStore.mutateAsync({
        website_url: draft.websiteUrl.trim() || null,
        contact_email: draft.contactEmail.trim() || null,
        contact_phone: draft.contactPhone.trim() || null,
        business_hours: draft.businessHours.trim() || null,
        phones: cleanedPhones,
      });
      // Reflect normalized state back into the form so rows trimmed/dropped
      // server-side don't reappear as "unsaved changes".
      setDraft((prev) => ({ ...prev, phones: cleanedPhones }));
      toast.success('Contact info saved');
    } catch {
      // hook already shows the toast
    }
  };

  const hasExtraPhones = draft.phones.length > 0;

  return (
    <div className="space-y-6">
      <div className="rounded-panel border border-line bg-surface p-6 shadow-s">
        <h3 className="font-display text-lg font-semibold text-ink">Store Contact</h3>
        <p className="mt-1 text-sm text-ink-2">
          The agent only shares contact details that are saved here. Leave a field blank if it doesn't apply — the agent will say it doesn't have that info rather than invent one.
        </p>
        {store?.whatsappNumber && (
          <p className="mt-2 text-xs text-ink-3">
            WhatsApp number is set elsewhere (currently <span className="font-mono text-ink">{store.whatsappNumber}</span>); the agent will share it automatically.
          </p>
        )}

        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-ink-2">Website</label>
            <input
              type="url"
              value={draft.websiteUrl}
              onChange={(e) => setDraft({ ...draft, websiteUrl: e.target.value })}
              placeholder="https://www.yourstore.com"
              className="mt-1 w-full rounded-xl border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
            />
            {errors.websiteUrl && <p className="mt-1 text-xs text-danger">{errors.websiteUrl}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-ink-2">Support email</label>
            <input
              type="email"
              value={draft.contactEmail}
              onChange={(e) => setDraft({ ...draft, contactEmail: e.target.value })}
              placeholder="support@yourstore.com"
              className="mt-1 w-full rounded-xl border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
            />
            {errors.contactEmail && <p className="mt-1 text-xs text-danger">{errors.contactEmail}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-ink-2">
              Phone number {hasExtraPhones && <span className="text-xs text-ink-3">(primary)</span>}
            </label>
            <input
              type="tel"
              value={draft.contactPhone}
              onChange={(e) => setDraft({ ...draft, contactPhone: e.target.value })}
              placeholder="+1 555 123 4567"
              className="mt-1 w-full rounded-xl border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
            />
            {hasExtraPhones && (
              <p className="mt-1 text-xs text-ink-3">
                Hidden by the additional phone numbers below — the agent uses the list instead.
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-ink-2">Business hours</label>
            <input
              type="text"
              value={draft.businessHours}
              onChange={(e) => setDraft({ ...draft, businessHours: e.target.value })}
              placeholder="Mon–Fri 9am–5pm WAT"
              className="mt-1 w-full rounded-xl border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
            />
            {errors.businessHours && <p className="mt-1 text-xs text-danger">{errors.businessHours}</p>}
          </div>
        </div>

        {/* Additional phone numbers */}
        <div className="mt-6 border-t border-line pt-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h4 className="text-sm font-semibold text-ink">Additional phone numbers</h4>
              <p className="mt-1 text-xs text-ink-3">
                Add separate lines for sales, support, branches, etc. When any are listed here, the agent
                shares this list and ignores the single primary phone above.
              </p>
            </div>
            <span className="shrink-0 text-xs text-ink-3">
              {draft.phones.length}/{MAX_STORE_PHONES}
            </span>
          </div>

          <div className="mt-3 space-y-2">
            {draft.phones.map((ph, idx) => {
              const e = phoneErrors[idx] ?? {};
              return (
                <div
                  key={idx}
                  className="grid grid-cols-1 gap-2 rounded-card border border-line bg-surface-2 p-3 sm:grid-cols-[1fr_1.5fr_auto] sm:items-start"
                >
                  <div>
                    <input
                      type="text"
                      value={ph.label}
                      onChange={(ev) => updatePhone(idx, 'label', ev.target.value)}
                      placeholder="Label (e.g. Sales)"
                      className="w-full rounded-xl border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                    />
                    {e.label && <p className="mt-1 text-xs text-danger">{e.label}</p>}
                  </div>
                  <div>
                    <input
                      type="tel"
                      value={ph.number}
                      onChange={(ev) => updatePhone(idx, 'number', ev.target.value)}
                      placeholder="+1 555 123 4567"
                      className="w-full rounded-xl border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                    />
                    {e.number && <p className="mt-1 text-xs text-danger">{e.number}</p>}
                  </div>
                  <button
                    type="button"
                    onClick={() => removePhone(idx)}
                    className="self-start rounded-md px-2 py-2 text-xs text-ink-3 hover:text-danger"
                    aria-label="Remove phone"
                  >
                    Remove
                  </button>
                </div>
              );
            })}
          </div>

          <div className="mt-3">
            <button
              type="button"
              onClick={addPhone}
              disabled={atPhoneCap}
              className="rounded-pill border border-line bg-surface-2 px-3 py-2 text-xs font-medium text-ink hover:bg-black/[0.04] disabled:cursor-not-allowed disabled:opacity-50"
            >
              + Add phone number
            </button>
          </div>
        </div>

        <div className="mt-5 flex items-center gap-3">
          <button
            onClick={handleSave}
            disabled={!dirty || hasErrors || updateStore.isPending}
            className="rounded-pill bg-brand px-4 py-2 text-sm font-medium text-white hover:bg-brand/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {updateStore.isPending ? 'Saving...' : 'Save'}
          </button>
          <span className="text-xs text-ink-3">{dirty ? 'Unsaved changes' : 'Saved'}</span>
        </div>
      </div>
    </div>
  );
}
